import { Box, Typography } from '@mui/material';
import { AppGlobalClassKeys } from 'app/appGlobalStyles';
import { MprcJoinComponents } from 'components';
import { MprcProgress } from "components/MprcProgress";
import { EnrollStatus } from 'generated/CoreApi_v1';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ProgressDetailsDialog } from './ProgressDetailDialog';

interface ProgressData {
    enrollmentId: string;
    progress: number | null;
    minProgress: number;
    status: EnrollStatus;
    isClosed: boolean;
    totalComponents: number;
    totalCompletedComponents: number;
    totalMandatoryComponents?: number;
    totalCompletedMandatoryComponents?: number;
}

interface IProps {
    variant: "inline" | "block";
    typographyVariant?: "subtitle1" | "caption";
    enableShowDetails?: boolean;
    data: ProgressData;
}

export const ProgressResult: React.FC<IProps> = (props) => {

    const { variant, typographyVariant = "subtitle1", enableShowDetails, data: { enrollmentId, progress, minProgress, status, isClosed, totalComponents, totalCompletedComponents, totalMandatoryComponents = 0, totalCompletedMandatoryComponents = 0 } } = props;

    const { t } = useTranslation("lms");

    const [showDetails, setShowDetails] = useState<boolean>(false);

    const color = status == EnrollStatus.WaitingScore
        ? "inherit"
        : status == EnrollStatus.Reproved
            ? "error"
            : progress != null && progress >= minProgress
                ? "success"
                : "primary";

    const RenderInline = (
        <>
            <Typography variant={typographyVariant} component="span" textTransform="uppercase">
                {t("Student.EnrollmentView.ProgressText")}
            </Typography>
            <Typography variant={typographyVariant} fontWeight="bold" component="span" color={`${color}.main`}>
                &nbsp;{progress || 0}%
            </Typography>
        </>
    );

    const RenderBlock = (
        <>
            <Typography variant={typographyVariant} component="span" textTransform="uppercase">
                {t("Student.EnrollmentView.ProgressText")}
            </Typography>
            &nbsp;
            <Typography
                variant={typographyVariant}
                fontWeight="bold"
                component="span"
                color={`${color}.main`}
            >
                {progress || 0}%
            </Typography>

            <MprcProgress
                value={progress || 0}
                color={color}
                backgroundColor="white"
                helperText={
                    !isClosed &&
                    <MprcJoinComponents
                        separatorVariant="newline"
                        components={[
                            t(`Student.EnrollmentView.CompletedItemsText${totalComponents == 1 ? "_one" : "_other"}`, { x: totalCompletedComponents, y: totalComponents }),
                            minProgress === 100
                                ? t("Student.EnrollmentView.MinFullProgressText")
                                : minProgress > 0
                                    ? t("Student.EnrollmentView.MinProgressText", { x: minProgress })
                                    : null,
                            totalMandatoryComponents > 0 ? <Typography variant="bodyNotice">{t(`Student.EnrollmentView.MandatoryItemsText${totalMandatoryComponents == 1 ? "_one" : "_other"}`, { x: totalCompletedMandatoryComponents, y: totalMandatoryComponents })}</Typography> : null
                        ]}
                    />

                }
            />
        </>
    );

    return (
        <>
            {enableShowDetails && showDetails &&
                <ProgressDetailsDialog
                    enrollmentId={enrollmentId}
                    onClose={() => setShowDetails(false)}
                />
            }
            <Box className={enableShowDetails ? AppGlobalClassKeys.clickable : undefined} onClick={() => enableShowDetails && setShowDetails(true)}>
                {variant == "inline" ? RenderInline : RenderBlock}
            </Box>
        </>
    );
};
